import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddTransportOrder() {
  const [fromLocation, setFromLocation] = useState("");
  const [toLocation, setToLocation] = useState("");
  const [fromLocations, setFromLocations] = useState([]);
  const [toLocations, setToLocations] = useState([]);
  const [toOrganizations, setToOrganizations] = useState([]);
  const [toOrganizationz, setToOrganizationz] = useState([]);
  const [transferOrders, setTransferOrders] = useState([]);
  const [filteredTransferOrders, setFilteredTransferOrders] = useState([]);
  const [filteredDispatchedStockOnLoan, setFilteredDispatchedStockOnLoan] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]); 
  const [dispatchedStockOnLoan, setDispatchedStockOnLoan] = useState([]); 
  const [selectedTransferOrders, setSelectedTransferOrders] = useState([]);
  const [selectedSalesOrders, setSelectedSalesOrders] = useState([]); 
  const [selectedDispatchedStockOnLoan, setSelectedDispatchedStockOnLoan] = useState([]);
  const [vehicleNo, setVehicleNo] = useState("");
  const [driverName, setDriverName] = useState("");
  const [status, setStatus] = useState("Drafted");
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState("transfer"); // "transfer", "sales", or "loan"
  const [hoveredOrderDetails, setHoveredOrderDetails] = useState(null);
  const [selectedToLocationFilter, setSelectedToLocationFilter] = useState("");
  const [selectedToOrganizationFilter, setSelectedToOrganizationFilter] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customers, setCustomers] = useState([]);
  const [addedClusterLocations, setAddedClusterLocations] = useState([]); // For loan tab
  const [selectedCluster, setSelectedCluster] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [clusters, setClusters] = useState([]);
  const [clusterOptions, setClusterOptions] = useState([]);
  const [organizationLocations, setOrganizationLocations] = useState([]);
  const [isLoanPrefilled, setIsLoanPrefilled] = useState(false);


  const navigate = useNavigate();

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please log in.");
        return;
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        const userData = response.data;
        setCurrentUser(userData);
  
        // Now that currentUser is set, fetch locations and transfer orders
        if (userData.role === "Farm User" || userData.role === "Company Admin") {
          fetchFilteredLocations(userData);
        } else if (userData.role === "Main Admin") {
          fetchAllLocations();
        }
  
        fetchTransferOrders();
        fetchSalesOrders();
        fetchStockOnLoan();
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data.");
      }
    };
  
    fetchCurrentUser();
    fetchCustomers();
    fetchAllOrganizations();
    fetchOrganizationsWithLocations();
  }, []); // Empty dependency array means this will run once when the component mounts  

  const fetchAllLocations = async () => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/locations`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setFromLocations(response.data.fromLocations);
    setToLocations(response.data.toLocations);
  };

  const fetchAllOrganizations = async () => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/organizations`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setToOrganizations(response.data.organizations);
  };
  
  const fetchOrganizationsWithLocations = async () => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/organizations/organizationLocations`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setToOrganizationz(response.data.results);
  };

  useEffect(() => {
    const fetchClusterLocations = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(`${API_BASE_URL}/clusters/clusterLocations`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const myOrgId = currentUser?.organization?._id || currentUser?.organization;

        // Filter out current user's organization from the clusters
        const filteredClusters = response.data.map((cluster) => {
          const filteredOrgs = cluster.organizations
            .filter((org) => org.organization_id !== myOrgId)
            .map((org) => ({
              ...org,
              locations: (org.locations || []).filter((loc) => !!loc._id),
            }));

          return {
            ...cluster,
            organizations: filteredOrgs,
          };
        }).filter(cluster => cluster.organizations.length > 0); // Only include clusters with valid organizations

        setClusters(filteredClusters);

        // Also update `addedClusterLocations` (flattened view used elsewhere)
        const allClusterLocations = filteredClusters.flatMap((cluster) =>
          cluster.organizations.flatMap((org) =>
            (org.locations || []).map((loc) => ({
              _id: loc._id,
              location_name: loc.location_name,
              organization: org.organization_id,
            }))
          )
        );

        // Deduplicate by location name
        const uniqueLocationsByName = Array.from(
          new Map(allClusterLocations.map((loc) => [loc.location_name, loc])).values()
        );

        setAddedClusterLocations(uniqueLocationsByName);
      } catch (error) {
        console.error("Error fetching cluster locations:", error);
        toast.error("Failed to fetch cluster locations.");
      }
    };

    if (currentUser) {
      fetchClusterLocations();
    }
  }, [currentUser]);

  const fetchFilteredLocations = async (userData) => {
    if (!userData) return; // Ensure userData is available before calling the API
  
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/products/filtered-to-from-locations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      if (userData.role === "Farm User") {
        setFromLocations(response.data.fromLocations || []);
        setToLocations(response.data.toLocations || []);
      } else {
        setFromLocations(response.data.fromLocations || []);
        setToLocations(response.data.toLocations || []);
      }
    } catch (error) {
      console.error("Error fetching filtered locations:", error);
      toast.error("Failed to fetch locations.");
    }
  };

  const fetchTransferOrders = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token missing. Please log in.");
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/transferOrders`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const draftedOrders = response.data.transferOrders.filter(order => order.status === "Drafted");
      setTransferOrders(draftedOrders);
      setFilteredTransferOrders(draftedOrders);
    } catch (error) {
      console.error("Error fetching transfer orders:", error);
      toast.error("Failed to fetch transfer orders.");
    }
  };

  const fetchSalesOrders = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token missing. Please log in.");
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/salesOrders/get/drafted`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSalesOrders(response.data.salesOrders);
    } catch (error) {
      console.error("Error fetching sales orders:", error);
      
      if (error.response && error.response.status === 404) {
        toast.info("No drafted sales orders available.");
      } else {
        toast.error("Failed to fetch sales orders.");
      }
    }
  };

  const fetchStockOnLoan = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token missing. Please log in.");
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/dispatchedStockOnLoan/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const draftedStockOnLoan = response.data.dispatchedStockOnLoan.filter(order => order.status === "Drafted");

      setDispatchedStockOnLoan(draftedStockOnLoan);
      setFilteredDispatchedStockOnLoan(draftedStockOnLoan);
    } catch (error) {
      console.error("Error fetching dispatched stock on loan:", error);
      
      if (error.response && error.response.status === 404) {
        toast.info("No drafted stock on loan available.");
      } else {
        toast.error("Failed to fetch stock on loan.");
      }
    }
  };

  const toggleTransferOrder = (order) => {
    if (selectedTransferOrders.some((o) => o._id === order._id)) {
      setSelectedTransferOrders(selectedTransferOrders.filter((o) => o._id !== order._id));
    } else {
      setSelectedTransferOrders([...selectedTransferOrders, order]);
    }
  };

  const toggleSalesOrder = (order) => {
    if (selectedSalesOrders.some((o) => o._id === order._id)) {
      setSelectedSalesOrders(selectedSalesOrders.filter((o) => o._id !== order._id));
    } else {
      setSelectedSalesOrders([...selectedSalesOrders, order]);
    }
  };

  const toggleStockOnLoan = (order) => {
    const alreadySelected = selectedDispatchedStockOnLoan.some((o) => o._id === order._id);

    if (alreadySelected) {
      setSelectedDispatchedStockOnLoan([]);
      setIsLoanPrefilled(false);
      setFromLocation("");
      setToLocation("");
      setVehicleNo("");
      setDriverName("");
      setSelectedCluster("");
      setSelectedOrg("");
    } else {
      setSelectedDispatchedStockOnLoan([order]);
      setFromLocation(order.from_location_id?._id || "");
      setToLocation(order.to_location_id?._id || "");
      setVehicleNo(order.vehicle_no || "");
      setDriverName(order.driver_name || "");
      setIsLoanPrefilled(true);

      // Auto-fill cluster and organization
      const targetLocationId = order.to_location_id?._id;
      let foundCluster = null;
      let foundOrganization = null;

      for (const cluster of clusters) {
        for (const org of cluster.organizations) {
          const hasLocation = org.locations?.some(loc => loc._id === targetLocationId);
          if (hasLocation) {
            foundCluster = cluster._id;
            foundOrganization = org.organization_id;
            break;
          }
        }
        if (foundCluster) break;
      }

      if (foundCluster && foundOrganization) {
        setSelectedCluster(foundCluster);
        setSelectedOrg(foundOrganization);
      } else {
        setSelectedCluster("");
        setSelectedOrg("");
      }
    }
  };

  const handleSubmit = async () => {
    if (!fromLocation || !toLocation || !vehicleNo || !driverName) {
      toast.error("Please fill all required fields.");
      return;
    }

    const payload = {
      from_location_id: fromLocation,
      to_location_id: toLocation,
      transfer_order_ids: selectedTransferOrders.map((o) => o._id),
      sales_order_ids: selectedSalesOrders.map((o) => o._id), 
      stock_on_loan_ids: selectedDispatchedStockOnLoan.map((o) => o._id),
      vehicle_no: vehicleNo,
      driver_name: driverName,
      status,
      created_by: currentUser?._id,
    };

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_BASE_URL}/transportOrders/add`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Transport order created successfully!");
      setTimeout(() => {
        window.location.reload(); // Reload page after 1 second
      }, 800);
      navigate("/transport-orders");
    } catch (error) {
      console.error("Error creating transport order:", error);
      
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to create transport order.");
      }
    }
  };

  const handleMouseEnter = (order) => {
    setHoveredOrderDetails(order); // Set order details on hover
  };

  const handleMouseLeave = () => {
    setHoveredOrderDetails(null); // Clear order details when hover ends
  };

  const handleToLocationChange = (e) => {
    const newToLocation = e.target.value;
    setToLocation(newToLocation);
  };

  const handleToLocationFilterChange = (e) => {
    const selectedLocation = e.target.value;
    setSelectedToLocationFilter(selectedLocation);

    // Filter the transfer orders based on the selected "To Location"
    if (selectedLocation) {
      setFilteredTransferOrders(
        transferOrders.filter((order) => order.to_location_id._id === selectedLocation)
      );
    } else {
      setFilteredTransferOrders(transferOrders); // If no filter, show all
    }
  };

  const handleToOrganizationFilterChange = (e) => {
    const selectedOrganization = e.target.value;
    setSelectedToOrganizationFilter(selectedOrganization);

    // Filter the transfer orders based on the selected "To Location"
    if (selectedOrganization) {
      setFilteredDispatchedStockOnLoan(
        dispatchedStockOnLoan.filter((order) => order.to_organization_id._id === selectedOrganization)
      );
    } else {
      setFilteredDispatchedStockOnLoan(dispatchedStockOnLoan); // If no filter, show all
    }
  };

  const fetchCustomers = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token missing. Please log in.");
      return;
    }
  
    try {
      const response = await axios.get(`${API_BASE_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      setCustomers(response.data.customers);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to fetch customers.");
    }
  };

  const filteredSalesOrders = selectedCustomer
  ? salesOrders.filter(order => order.customer_id._id === selectedCustomer)
  : salesOrders;
  
  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-6">Add Transport Order</h2>
        <div className="mb-4">
          <button
            className={`px-4 py-2 rounded-lg ${activeTab === "transfer" ? "bg-blue-700 text-white" : "bg-gray-300"}`}
            onClick={() => setActiveTab("transfer")}
          >
            Transfer Orders
          </button>

          <button
            className={`ml-4 px-4 py-2 rounded-lg ${activeTab === "sales" ? "bg-blue-700 text-white" : "bg-gray-300"}`}
            onClick={() => setActiveTab("sales")}
          >
            Sales Orders
          </button>

          <button
            className={`ml-4 px-4 py-2 rounded-lg ${activeTab === "loan" ? "bg-blue-700 text-white" : "bg-gray-300"}`}
            onClick={() => setActiveTab("loan")}
          >
            Stock on Loan
          </button>
        </div>
        <div className="grid grid-cols-2 gap-8">
          {activeTab === "sales" && (
            <div className="border-2 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Available Sales Orders</h3>
              <label className="block mt-4 mb-2 font-semibold">Filter by Customer</label>
              <select
                className="w-full p-3 border rounded-lg"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <option value="">All Customers</option>
                {customers.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {customer.customer_name} ({customer.customer_code})
                  </option>
                ))}
              </select>

              <ul className="border p-4 rounded-lg mt-4">
                {filteredSalesOrders.map((order) => (
                  <li
                    key={order._id}
                    className="flex justify-between items-center mb-2 border-b pb-2"
                    onMouseEnter={() => handleMouseEnter(order)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {order.sales_order_id}
                    <button
                      className={`px-3 py-1 rounded ${
                        selectedSalesOrders.some((o) => o._id === order._id)
                          ? "bg-red-500 text-white"
                          : "bg-green-500 text-white"
                      }`}
                      onClick={() => toggleSalesOrder(order)}
                    >
                      {selectedSalesOrders.some((o) => o._id === order._id) ? "Remove" : "Add"}
                    </button>

                    {hoveredOrderDetails && hoveredOrderDetails._id === order._id && (
                      <div className="absolute bg-white border p-4 shadow-lg mt-2 rounded-lg w-60">
                        <h4 className="text-center font-bold mb-2">Details</h4>
                        <p><strong>ID:</strong> {order.sales_order_id}</p>
                        <p><strong>Order Request ID:</strong> {order.order_request_id}</p>
                        <p><strong>Customer:</strong> {order.customer_id.customer_name}</p>
                        <p><strong>Final Amount:</strong> {order.total_amount}</p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === "transfer" && (
            <div className="border-2 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Available Transfer Orders</h3>
              <label className="block mb-2 font-semibold">Filter by To Location</label>
              <select
                className="w-full p-3 border rounded-lg"
                value={selectedToLocationFilter}
                onChange={handleToLocationFilterChange}
              >
                <option value="">All Locations</option>
                {toLocations.map((location) => (
                  <option key={location._id} value={location._id}>
                    {location.location_name}
                  </option>
                ))}
              </select>
              <ul className="border p-4 rounded-lg mt-4">
                {filteredTransferOrders.map((order) => (
                  <li
                    key={order._id}
                    className="flex justify-between items-center mb-2 border-b pb-2"
                    onMouseEnter={() => handleMouseEnter(order)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {order.transfer_order_code}
                    <button
                      className={`px-3 py-1 rounded ${
                        selectedTransferOrders.some((o) => o._id === order._id)
                          ? "bg-red-500 text-white"
                          : "bg-green-500 text-white"
                      }`}
                      onClick={() => toggleTransferOrder(order)}
                    >
                      {selectedTransferOrders.some((o) => o._id === order._id) ? "Remove" : "Add"}
                    </button>
                    {hoveredOrderDetails && hoveredOrderDetails._id === order._id && (
                      <div className="absolute bg-white border p-4 shadow-lg mt-2 rounded-lg w-60">
                        <h4 className="text-center font-bold mb-2">Details</h4>
                        <p><strong>Code:</strong> {order.transfer_order_code}</p>
                        <p><strong>Status:</strong> {order.status}</p>
                        <p><strong>Destination:</strong> {order.to_location_id.location_name}</p>
                        <p><strong>Author:</strong> {order.created_by.username}</p>
                        <p><strong>Date:</strong> {new Date(order.transfer_order_date).toLocaleDateString("en-GB")}</p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === "loan" && (
            <div className="border-2 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Available Stock on Loan</h3>
              <label className="block mb-2 font-semibold">Filter by To Organization</label>
              <select
                className="w-full p-3 border rounded-lg"
                value={selectedToOrganizationFilter}
                onChange={handleToOrganizationFilterChange}
              >
                <option value="">All Organizations</option>
                {toOrganizations.map((organization) => (
                  <option key={organization._id} value={organization._id}>
                    {organization.organization_name}
                  </option>
                ))}
              </select>
              <ul className="border p-4 rounded-lg mt-4">
                {filteredDispatchedStockOnLoan.map((loan) => (
                  <li
                    key={loan._id}
                    className="flex justify-between items-center mb-2 border-b pb-2"
                    onMouseEnter={() => handleMouseEnter(loan)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {loan.dispatched_stock_on_loan_code}
                    <button
                      className={`px-3 py-1 rounded ${
                        selectedDispatchedStockOnLoan.some((s) => s._id === loan._id)
                          ? "bg-red-500 text-white"
                          : "bg-green-500 text-white"
                      }`}
                      onClick={() => toggleStockOnLoan(loan)}
                    >
                      {selectedDispatchedStockOnLoan.some((s) => s._id === loan._id) ? "Remove" : "Add"}
                    </button>
                    {hoveredOrderDetails && hoveredOrderDetails._id === loan._id && (
                      <div className="absolute bg-white border p-4 shadow-lg mt-2 rounded-lg w-60">
                        <h4 className="text-center font-bold mb-2">Details</h4>
                        <p><strong>Code:</strong> {loan.dispatched_stock_on_loan_code}</p>
                        <p><strong>To Organization :</strong> {loan.to_organization_id?.organization_name}</p>
                        <p><strong>To Location:</strong> {loan.to_location_id?.location_name || "Unknown"} </p>
                        <p><strong>Status:</strong> {loan.status}</p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Transport Order Form */}
          <div className="border-2 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Transport Order Details</h3>
            <label className="block mb-2">From Location</label>
            <select
              className="w-full p-3 border rounded-lg"
              value={fromLocation}
              onChange={(e) => setFromLocation(e.target.value)}
            >
              <option value="" disabled>Select From Location</option>
              {fromLocations
                .filter((location) => location._id !== toLocation)
                .map((location) => (
                  <option key={location._id} value={location._id}>
                    {location.location_name}
                  </option>
                ))}
            </select>
            {activeTab === "loan" && (
            <>
              <label className="block mt-4 mb-2">Cluster</label>
              <select
                className="w-full p-3 border rounded-lg"
                value={selectedCluster}
                onChange={(e) => setSelectedCluster(e.target.value)}
              >
                <option value="" disabled>Select Cluster</option>
                {clusters.map((cluster) => (
                  <option key={cluster._id} value={cluster._id}>
                    {cluster.cluster_name}
                  </option>
                ))}
              </select>

              <label className="block mt-4 mb-2">Organization</label>
              <select
                className="w-full p-3 border rounded-lg"
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
              >
                <option value="" disabled>Select Organization</option>
                {clusters
                  .find((c) => c._id === selectedCluster)?.organizations
                  .map((org) => (
                    <option key={org.organization_id} value={org.organization_id}>
                      {org.organization_name}
                    </option>
                  ))}
              </select>
            </>
          )}
          <label className="block mt-4 mb-2">Final Destination</label>
          <select
            className="w-full p-3 border rounded-lg"
            value={toLocation}
            onChange={handleToLocationChange}
          >
            <option value="" disabled>Select Final Destination</option>
            {(activeTab === "loan"
              ? addedClusterLocations.filter(
                  (loc) => loc.organization === selectedOrg
                )
              : toLocations
            )
              .filter((loc) => loc._id !== fromLocation)
              .map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.location_name}
                </option>
              ))}
          </select>
            <label className="block mt-4 mb-2">Vehicle No</label>
            <input
              type="text"
              className="w-full p-3 border rounded-lg"
              value={vehicleNo}
              onChange={(e) => setVehicleNo(e.target.value)}
            />
            <label className="block mt-4 mb-2">Driver Name</label>
            <input
              type="text"
              className="w-full p-3 border rounded-lg"
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
            />
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Selected Orders</h3>
              <ul className="border p-4 rounded-lg">
                {(activeTab === "sales"
                  ? selectedSalesOrders
                  : activeTab === "transfer"
                  ? selectedTransferOrders
                  : selectedDispatchedStockOnLoan
                ).map((order) => (
                  <li key={order._id} className="flex justify-between items-center mb-2 border-b pb-2">
                    {/* Display the correct identifier */}
                    {order.sales_order_id || order.transfer_order_code || order.dispatched_stock_on_loan_code}

                    <button
                      className="bg-red-500 text-white px-3 py-1 rounded"
                      onClick={() => {
                        if (activeTab === "sales") {
                          toggleSalesOrder(order);
                        } else if (activeTab === "transfer") {
                          toggleTransferOrder(order);
                        } else {
                          toggleStockOnLoan(order);
                        }
                      }}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="text-right mt-6">
          <button
            onClick={handleSubmit}
            className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-blue-600"
          >
            Submit Transport Order
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddTransportOrder;
